const LOOPBACK_HOSTS = new Set(['127.0.0.1', 'localhost', '::1', '[::1]']);

const STAGING_PROJECT_ID = 'tms-drayage-staging';
const PRODUCTION_PROJECT_ID = 'tms-drayage-dev';
const STAGING_APP_ID = '1:1077327828420:web:1abd587d33276f54d28091';
const STAGING_HOSTS = new Set([
  'tms-drayage-staging.web.app',
  'tms-drayage-staging.firebaseapp.com',
]);
const PRODUCTION_HOSTS = new Set([
  'aytms.com',
  'www.aytms.com',
  'tms-drayage-dev.web.app',
  'tms-drayage-dev.firebaseapp.com',
]);
const STAGING_CHANNEL_HOST = /^tms-drayage-staging--release-[a-z0-9-]+\.web\.app$/;
const REQUIRED_CONFIG_FIELDS = Object.freeze([
  'projectId',
  'appId',
  'apiKey',
  'authDomain',
  'storageBucket',
  'messagingSenderId',
]);

function expectedProjectForHost(hostname) {
  if (STAGING_HOSTS.has(hostname) || STAGING_CHANNEL_HOST.test(hostname)) {
    return STAGING_PROJECT_ID;
  }
  if (PRODUCTION_HOSTS.has(hostname)) {
    return PRODUCTION_PROJECT_ID;
  }
  return null;
}

function isValidFirebaseConfig(config) {
  return config
    && typeof config === 'object'
    && REQUIRED_CONFIG_FIELDS.every((field) => typeof config[field] === 'string' && config[field].trim() !== '');
}

function blocked(reason) {
  return { mode: 'blocked', reason };
}

// Loopback emulator sessions may override ports so parallel local suites do not
// collide; overrides are ignored for every non-loopback origin.
function emulatorPort(params, key, fallback) {
  const value = Number.parseInt(params.get(key) ?? '', 10);
  return Number.isInteger(value) && value > 0 && value < 65536 ? value : fallback;
}

export async function resolveFirebaseRuntime(locationLike, fetchImpl = globalThis.fetch) {
  const hostname = String(locationLike?.hostname || '').toLowerCase();
  const loopback = LOOPBACK_HOSTS.has(hostname);
  const params = new URLSearchParams(String(locationLike?.search || ''));
  const emulatorRequested = params.get('emulator') === '1';
  if (emulatorRequested && !loopback) {
    return blocked('emulator-mode-requires-loopback');
  }
  if (loopback && !emulatorRequested) {
    return blocked('explicit-emulator-mode-required');
  }
  if (emulatorRequested) {
    return {
      mode: 'emulator',
      firebaseConfig: {
        projectId: 'demo-tms',
        apiKey: 'demo-tms-emulator-only',
        appId: '1:000000000000:web:demo-tms-emulator',
        authDomain: '127.0.0.1',
        storageBucket: 'demo-tms.appspot.com',
        messagingSenderId: '000000000000',
      },
      endpoints: {
        auth: `http://127.0.0.1:${emulatorPort(params, 'authPort', 9099)}`,
        firestore: ['127.0.0.1', emulatorPort(params, 'fsPort', 8080)],
        functions: ['127.0.0.1', emulatorPort(params, 'fnPort', 5001)],
        storage: ['127.0.0.1', emulatorPort(params, 'storagePort', 9199)],
      },
    };
  }

  const expectedProjectId = expectedProjectForHost(hostname);
  if (!expectedProjectId) {
    return blocked('unknown-remote-host');
  }
  if (String(locationLike?.protocol || '').toLowerCase() !== 'https:') {
    return blocked('remote-origin-requires-https');
  }
  if (typeof fetchImpl !== 'function') {
    return blocked('firebase-config-fetch-failed');
  }

  let response;
  try {
    response = await fetchImpl('/__/firebase/init.json', {
      cache: 'no-store',
      credentials: 'same-origin',
    });
  } catch {
    return blocked('firebase-config-fetch-failed');
  }
  if (!response?.ok) {
    return blocked('firebase-config-response-invalid');
  }

  let firebaseConfig;
  try {
    firebaseConfig = await response.json();
  } catch {
    return blocked('firebase-config-response-invalid');
  }

  if (!isValidFirebaseConfig(firebaseConfig)) {
    return blocked('firebase-config-invalid');
  }
  if (firebaseConfig.projectId !== expectedProjectId) {
    return blocked('firebase-config-project-mismatch');
  }
  if (expectedProjectId === STAGING_PROJECT_ID && firebaseConfig.appId !== STAGING_APP_ID) {
    return blocked('firebase-config-staging-app-mismatch');
  }
  return { mode: 'remote', firebaseConfig };
}
